import { useCallback, useLayoutEffect, useMemo, useState } from "react";

function isLeaving() {
  return (
    !document["fullscreenElement"] &&
    !document["webkitFullscreenElement"] &&
    !document["mozFullscreenElement"] &&
    !document["msFullscreenElement"]
  );
}

const prefix = {
  safari: {
    enter: "webkitRequestFullscreen",
    exit: "webkitExitFullscreen",
    escape: "onwebkitfullscreenchange",
  },
  classic: {
    enter: "requestFullscreen",
    exit: "exitFullscreen",
    escape: "onfullscreenchange",
  },
};

const isSafari = () => {
  const { userAgent } = navigator;
  return userAgent.match(/safari/i) && !userAgent.match(/chrome/i);
};

export default function useFullscreen(
  ref: React.MutableRefObject<HTMLDivElement>
): {
  isFullscreen: boolean;
  openFullscreen: () => void;
  closeFullscreen: () => void;
} {
  const [fullscreen, setFullscreen] = useState(false);
  const [isFullscreenReady, setIsFullscreenReady] = useState(false);
  const browser = useMemo(
    () => (isSafari() ? prefix.safari : prefix.classic),
    []
  );

  useLayoutEffect(() => {
    document[browser.escape] = () => {
      if (isLeaving()) {
        setFullscreen(false);
        setIsFullscreenReady(false);
      }
    };
    return () => (document[browser.escape] = undefined);
  }, [browser.escape, fullscreen, isFullscreenReady, ref]);

  const openFullscreen = useCallback(async () => {
    try {
      if (ref.current) {
        await ref.current[browser.enter]();
        return setIsFullscreenReady(true);
      }
    } catch {
      setIsFullscreenReady(false);
    }
  }, [browser, ref]);

  const closeFullscreen = useCallback(() => {
    document[browser.exit]();
    setFullscreen(false);
    setIsFullscreenReady(false);
  }, [browser.exit]);

  return {
    isFullscreen: isFullscreenReady,
    openFullscreen,
    closeFullscreen,
  };
}
